import { mkdir, writeFile, readFile } from "node:fs/promises"
import { join, relative, dirname } from "node:path"
import { deriveContextKey } from "../scanner/paths.js"
import { walkDir } from "../scanner/walk.js"

export const OUTPUT_DIR = ".clove"
export const OUTPUT_FILE = "types.d.ts"

export interface CodegenOptions {
  /** Project root — the generated directory is created here. */
  rootDir: string
  /** Directory containing `services/` and `di/`. */
  sourceDir: string
  servicesDir?: string
  diDir?: string
}

interface Entry {
  key: string
  /** Import specifier relative to the generated file. */
  specifier: string
  kind: "service" | "di"
}

/**
 * Emits `.clove/types.d.ts`, which augments the `Ctx` interface with one
 * property per file in `services/` and `di/`.
 *
 * This is a pure path-level scan: files are never evaluated, so codegen stays
 * fast and cannot be broken by a module that throws at import time.
 */
export async function generateTypes(options: CodegenOptions): Promise<string> {
  const { rootDir, sourceDir } = options
  const servicesDir = join(sourceDir, options.servicesDir ?? "services")
  const diDir = join(sourceDir, options.diDir ?? "di")
  const outDir = join(rootDir, OUTPUT_DIR)
  const outFile = join(outDir, OUTPUT_FILE)

  const entries: Entry[] = []
  const seen = new Map<string, string>()

  for (const [dir, kind] of [
    [servicesDir, "service"],
    [diDir, "di"],
  ] as const) {
    for (const file of await walkDir(dir)) {
      const key = deriveContextKey(file.relative)
      const previous = seen.get(key)
      if (previous) {
        // Two files claiming one key is a boot error; the generated types just
        // keep the first so the file stays valid TypeScript for the editor.
        continue
      }
      seen.set(key, file.absolute)
      entries.push({
        key,
        specifier: importSpecifier(outDir, file.absolute),
        kind,
      })
    }
  }

  const contents = render(entries)

  await mkdir(outDir, { recursive: true })
  const existing = await readFile(outFile, "utf8").catch(() => null)
  if (existing !== contents) {
    await writeFile(outFile, contents, "utf8")
  }
  return outFile
}

function render(entries: Entry[]): string {
  const lines: string[] = [
    "// Generated by CloveJS. Do not edit — this file is rewritten on every",
    "// dev-server start, file change and build.",
    "",
  ]

  if (entries.length === 0) {
    lines.push(
      'declare module "clovejs" {',
      "  // No services or di values found yet.",
      "  // eslint-disable-next-line @typescript-eslint/no-empty-object-type",
      "  interface Ctx {}",
      "}",
      "",
      "export {}",
      "",
    )
    return lines.join("\n")
  }

  const emitLogger = !entries.some((e) => e.key === "logger")
  const helpers = [
    entries.some((e) => e.kind === "service") && "CloveService",
    entries.some((e) => e.kind === "di") && "CloveDi",
    emitLogger && "Logger",
  ].filter((name): name is string => Boolean(name))

  lines.push(`import type { ${helpers.join(", ")} } from "clovejs"`)
  for (const entry of entries) {
    lines.push(`import type ${identifier(entry.key)} from "${entry.specifier}"`)
  }
  lines.push("", 'declare module "clovejs" {', "  interface Ctx {")

  if (emitLogger) {
    lines.push("    /** The built-in logger. Override it with services/logger.ts. */")
    lines.push("    logger: Logger")
  }

  for (const entry of entries) {
    const wrapper = entry.kind === "service" ? "CloveService" : "CloveDi"
    lines.push(`    ${propertyName(entry.key)}: ${wrapper}<typeof ${identifier(entry.key)}>`)
  }

  lines.push("  }", "}", "", "export {}", "")
  return lines.join("\n")
}

/** A TypeScript-safe local alias for an imported definition. */
function identifier(key: string): string {
  return `__clove_${key.replace(/[^A-Za-z0-9_$]/g, "_")}`
}

function propertyName(key: string): string {
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key) ? key : JSON.stringify(key)
}

function importSpecifier(fromDir: string, target: string): string {
  let rel = relative(fromDir, target).split("\\").join("/")
  if (!rel.startsWith(".")) rel = "./" + rel
  // TypeScript resolves the declaration from the source file, so the extension
  // is dropped rather than rewritten to .js.
  return rel.replace(/\.(m|c)?tsx?$/, "").replace(/\.(m|c)?jsx?$/, "")
}

/**
 * Returns a warning when tsconfig.json cannot see the generated types file,
 * or null when it can (or when there is no tsconfig to judge).
 *
 * TypeScript never expands an include wildcard into a directory whose name
 * starts with a dot, so `"include": [".clove"]` matches nothing — the file
 * must be named by an explicit pattern such as `.clove/**\/*`. The failure is
 * silent: the augmentation is simply absent and `ctx` falls back to
 * `Record<string, any>` everywhere.
 */
export async function tsconfigIncludeWarning(rootDir: string): Promise<string | null> {
  const raw = await readFile(join(rootDir, "tsconfig.json"), "utf8").catch(() => null)
  if (raw === null) return null

  let config: unknown
  try {
    config = JSON.parse(raw)
  } catch {
    // Comments or trailing commas — legal in a tsconfig, but this check is
    // best-effort and stays quiet rather than guessing.
    return null
  }
  if (typeof config !== "object" || config === null) return null

  const patterns = ["files", "include"].flatMap((field) => {
    const value = (config as Record<string, unknown>)[field]
    return Array.isArray(value) ? value.filter((p): p is string => typeof p === "string") : []
  })

  const covered = patterns.some((p) =>
    p.replace(/^\.\//, "").startsWith(`${OUTPUT_DIR}/`) &&
    p.replace(/^\.\//, "") !== `${OUTPUT_DIR}/`,
  )
  if (covered) return null

  return (
    `tsconfig.json does not include ${OUTPUT_DIR}/${OUTPUT_FILE}, so ctx stays untyped. ` +
    `TypeScript skips dot-directories unless a pattern names their contents explicitly — ` +
    `add "${OUTPUT_DIR}/**/*" to "include" (a bare "${OUTPUT_DIR}" entry matches nothing).`
  )
}

/** Ensures the project's .gitignore covers the generated directory. */
export async function ensureGitignore(rootDir: string): Promise<void> {
  const path = join(rootDir, ".gitignore")
  const existing = await readFile(path, "utf8").catch(() => null)
  if (existing === null) return
  if (existing.split("\n").some((line) => line.trim() === `${OUTPUT_DIR}/`)) return
  await mkdir(dirname(path), { recursive: true })
  await writeFile(
    path,
    existing.replace(/\n*$/, "\n") + `\n# CloveJS generated types\n${OUTPUT_DIR}/\n`,
    "utf8",
  )
}
